import { useEffect, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  investigationHighlightedStationIdsAtom,
  type IRideInvestigationHistoryDto,
} from "@ryde/shared";

import {
  buildHistoryRows,
  buildLifecycleSteps,
  countHistoryRows,
  filterHistoryRows,
  findActiveHistoryRowId,
} from "./investigation.history.utils";
import type {
  IInvestigationHistoryRow,
  IPlaybackModel,
  TInvestigationHistoryFilter,
} from "./investigation.models";
import { usePlaybackContext } from "./playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "./playback/use-playback-snapshot";

/** Rows, counts, lifecycle, the active filter/row and row selection for the Timeline tab. */
export const useInvestigationHistoryView = ({
  model,
  history,
}: {
  model: IPlaybackModel;
  history?: IRideInvestigationHistoryDto;
}) => {
  const [filter, setFilter] = useState<TInvestigationHistoryFilter>("all");
  const snapshot = usePlaybackSnapshot();
  const { seekToMs } = usePlaybackContext();
  const setHighlightedStationIds = useSetRecoilState(
    investigationHighlightedStationIdsAtom,
  );
  const currentMs = snapshot?.tMs;

  const allRows = useMemo(
    () => buildHistoryRows({ model, history }),
    [model, history],
  );
  const counts = useMemo(() => countHistoryRows(allRows), [allRows]);
  const rows = useMemo(
    () => filterHistoryRows({ rows: allRows, filter }),
    [allRows, filter],
  );
  const lifecycle = useMemo(
    () =>
      history
        ? buildLifecycleSteps({ model, history, nowMs: Date.now() })
        : undefined,
    [model, history],
  );
  const activeRowId = useMemo(
    () => findActiveHistoryRowId({ rows, currentMs }),
    [rows, currentMs],
  );

  // Selecting a row moves the replay to its moment and rings the stops it is about.
  const selectRow = useEvent((row: IInvestigationHistoryRow) => {
    seekToMs(row.tMs);
    setHighlightedStationIds(row.stationIds ?? []);
  });

  // The rings belong to the Timeline: leaving it (another tab, another ride, closing) clears them.
  useEffect(
    () => () => setHighlightedStationIds([]),
    [setHighlightedStationIds],
  );

  return { filter, setFilter, rows, counts, lifecycle, activeRowId, selectRow };
};
